import { AbstractTeaserList, BaseTeaserItem } from '../../../../Resources/Private/TypeScript/Components/AbstractTeaserList'
import { getElementChild, getElements } from '../../../../Resources/Private/TypeScript/Utility/Dom'

interface Author {
  imageVariants: AuthorImageVariants
  name: string
  teaserImageData: {
    alternative: null | string
    description: null | string
    title: null | string
  }
  uid: number
}

interface AuthorImageVariants {
  '48x48': null | string
  '48x48_2x': null | string
}

interface NewsItem extends BaseTeaserItem {
  author: Author | null
  date: string
  introText: string
  publishDate: number
  readingTime: number
}

class NewsList extends AbstractTeaserList<NewsItem> {
  constructor(container: HTMLElement) {
    super(container, {
      apiUrl: `${container.dataset.apiUrl || ''}/news`,
      containerSelector: 'news-list',
      itemsPerPage: 6,
      listSelector: 'news-list-items',
      templateSelector: 'news-item-template'
    })
  }

  protected createItem(newsItem: NewsItem): HTMLElement | null {
    if (this.template === null) {
      return null
    }

    const item = this.template.content.firstElementChild?.cloneNode(true) as HTMLElement | null

    if (item === null) {
      return null
    }

    const picture = getElementChild(item, 'news-item-template-image')
    if (picture !== null) {
      this.updatePictureSources(picture, newsItem)
    }

    const badge = getElementChild(item, 'news-item-template-badge')
    if (badge !== null && newsItem.category !== null) {
      badge.className = `badge badge--${newsItem.category.color}`
      badge.textContent = newsItem.category.title
    } else {
      badge?.remove()
    }

    const link = getElementChild(item, 'news-item-template-link') as HTMLAnchorElement | null
    if (link !== null) {
      link.href = `${newsItem.detailLink}/${newsItem.slug}`
      link.textContent = newsItem.teaserTitle || newsItem.title
    }

    const text = getElementChild(item, 'news-item-template-text')
    if (text !== null) {
      text.textContent = this.truncateText(newsItem.introText, 80)
    }

    const authorBox = getElementChild(item, 'news-item-template-author')
    if (authorBox !== null && newsItem.author !== null) {
      this.updateAuthorBox(authorBox, newsItem)
    } else if (authorBox !== null) {
      authorBox.remove()
    }

    return item
  }

  private updateAuthorBox(authorBox: HTMLElement, newsItem: NewsItem): void {
    const author = newsItem.author

    if (author === null) {
      return
    }

    const authorImg = getElementChild(authorBox, 'news-item-template-author-image') as HTMLImageElement | null
    if (
      authorImg !== null &&
      author.imageVariants['48x48'] !== null &&
      author.imageVariants['48x48_2x'] !== null
    ) {
      authorImg.src = author.imageVariants['48x48']
      authorImg.srcset = `${author.imageVariants['48x48']} 1x, ${author.imageVariants['48x48_2x']} 2x`
      authorImg.alt = author.name
    } else {
      authorImg?.remove()
    }

    const authorName = getElementChild(authorBox, 'news-item-template-author-name')
    if (authorName !== null) {
      authorName.textContent = author.name
    }

    const dateElement = getElementChild(authorBox, 'news-item-template-date')
    if (dateElement !== null) {
      dateElement.textContent = newsItem.date
    }

    const readingTimeElement = getElementChild(authorBox, 'news-item-template-reading-time')
    if (readingTimeElement !== null && newsItem.readingTime > 0) {
      readingTimeElement.textContent = `${newsItem.readingTime} ${readingTimeElement.dataset.label || 'min'}`
    } else {
      const dot = getElementChild(authorBox, 'news-item-template-author-dot')
      dot?.remove()
      readingTimeElement?.remove()
    }
  }
}

const containers = getElements('news-list')
containers.forEach((container: HTMLElement): void => {
  new NewsList(container)
})
